/**
 * Meta (Facebook) Pixel wrapper.
 *
 * Loads the Pixel base script lazily on init, exposes typed wrappers around
 * the global `fbq()` function, and stays a no-op when the Pixel ID is not
 * configured (mirrors the Firebase Analytics behaviour in `analytics.ts`).
 *
 * Conversion events fired through this module should pass an `eventID` so
 * the corresponding server-side Conversions API event can be deduped.
 */

declare global {
  interface Window {
    fbq?: FbqFunction
    _fbq?: FbqFunction
  }
}

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  push?: FbqFunction
  loaded?: boolean
  version?: string
  queue?: unknown[][]
}

/** Meta standard conversion events used by Coupling WNC. */
export type MetaStandardEvent =
  | 'PageView'
  | 'Lead'
  | 'Schedule'
  | 'CompleteRegistration'
  | 'ViewContent'
  | 'Contact'

let initialized = false

/**
 * Inject the Meta Pixel base script and bind the given Pixel ID.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initMetaPixel(pixelId: string): void {
  if (initialized || typeof window === 'undefined') return
  if (window.fbq) {
    initialized = true
    return
  }

  // Standard Meta Pixel snippet (TS-flavoured).
  // Builds a queue-backed shim until fbevents.js loads and replaces it.
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      fbq.queue?.push(args)
    }
  } as FbqFunction
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  window.fbq = fbq
  if (!window._fbq) window._fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const firstScript = document.getElementsByTagName('script')[0]
  firstScript?.parentNode?.insertBefore(script, firstScript)

  window.fbq('init', pixelId)
  // Skip initial PageView — RouteAnalytics.tsx will fire it on mount,
  // avoiding duplicate PageView events on initial load.
  initialized = true
}

/** Fire a Meta standard PageView. Used on SPA route changes. */
export function trackMetaPageView(): void {
  if (!window.fbq) return
  window.fbq('track', 'PageView')
}

/**
 * Fire a Meta standard event.
 *
 * @param eventName One of the Meta standard event names.
 * @param params Event parameters (e.g. `{ value, currency, content_name }`).
 * @param eventID Optional UUID for client/server dedup with the Conversions API.
 */
export function trackMetaStandard(
  eventName: MetaStandardEvent,
  params: Record<string, unknown> = {},
  eventID?: string,
): void {
  if (!window.fbq) return
  if (eventID) {
    window.fbq('track', eventName, params, { eventID })
  } else {
    window.fbq('track', eventName, params)
  }
}

/**
 * Fire a Meta custom event. Use sparingly — Meta's optimizer needs ≥50
 * conversions/event/week to leave learning phase, so prefer reusing one of
 * the standard events when the action fits.
 */
export function trackMetaCustom(
  eventName: string,
  params: Record<string, unknown> = {},
  eventID?: string,
): void {
  if (!window.fbq) return
  if (eventID) {
    window.fbq('trackCustom', eventName, params, { eventID })
  } else {
    window.fbq('trackCustom', eventName, params)
  }
}

export function isMetaPixelInitialized(): boolean {
  return initialized
}
